import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, Navigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { acceptInviteSchema, type AcceptInviteInput, type LoginResponse } from '@koyomi/shared';
import { Button, Input, MaskedInput } from '@koyomi/ui';
import { AuthCard } from './AuthCard.tsx';
import { api, ApiError } from '../../lib/api.ts';
import { useAuthStore } from '../../app/auth-store.ts';

interface InviteInfo { email: string; business: string; role: string; existingUser: boolean; phoneMasked: string | null }

export function InvitePage() {
  const { t } = useTranslation(['auth', 'common']);
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { setSession, status } = useAuthStore();
  const info = useQuery({ queryKey: ['invite', token], queryFn: () => api<InviteInfo>('/auth/invite', { query: { token }, global: true }), enabled: !!token, retry: false });
  const form = useForm<AcceptInviteInput>({ resolver: zodResolver(acceptInviteSchema), defaultValues: { token, name: undefined, password: '', phone: '' } });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const res = await api<LoginResponse>('/auth/invite/accept', { body: { ...values, name: values.name || undefined, phone: values.phone || undefined }, global: true });
      setSession(res);
    } catch (err) {
      form.setError('root', { message: err instanceof ApiError ? err.message : t('common:errors.INTERNAL') });
    }
  });

  if (status === 'authenticated' && form.formState.isSubmitSuccessful) return <Navigate to="/" replace />;
  if (info.isLoading) return <AuthCard title={t('auth:invite.title')}><span className="k-spinner" /></AuthCard>;
  if (info.isError || !token) return <AuthCard title={t('auth:invite.title')}><p className="k-field__error">{t('auth:invite.invalid')}</p></AuthCard>;

  // E-mail já tem conta: não é cadastro, é login para aceitar o convite (nome não muda; telefone só se estiver vazio).
  const existing = !!info.data?.existingUser;
  return (
    <AuthCard title={t(existing ? 'auth:invite.existingTitle' : 'auth:invite.title')} subtitle={t(existing ? 'auth:invite.existingSubtitle' : 'auth:invite.subtitle', { business: info.data?.business })}>
      <form className="k-stack" onSubmit={onSubmit} noValidate>
        <Input label={t('auth:login.email')} value={info.data?.email ?? ''} readOnly />
        {!existing && <Input label={t('auth:invite.name')} autoFocus {...form.register('name')} error={form.formState.errors.name && t('common:errors.VALIDATION_ERROR')} />}
        {existing && info.data?.phoneMasked
          ? <Input label={t('auth:invite.phone')} value={info.data.phoneMasked} readOnly hint={t('auth:invite.phoneKeepHint')} />
          : <MaskedInput mask="phone" label={t('auth:invite.phone')} hint={t('auth:invite.phoneHint')} {...form.register('phone')} />}
        <Input label={t(existing ? 'auth:invite.currentPassword' : 'auth:login.password')} type="password" autoFocus={existing} autoComplete={existing ? 'current-password' : 'new-password'} {...form.register('password')} error={form.formState.errors.password && t('common:errors.VALIDATION_ERROR')} />
        {form.formState.errors.root && <div className="k-field__error" role="alert">{form.formState.errors.root.message}</div>}
        <Button type="submit" variant="primary" size="lg" block loading={form.formState.isSubmitting}>{t(existing ? 'auth:invite.existingSubmit' : 'auth:invite.submit')}</Button>
        {existing && <Link to="/forgot-password" style={{ textAlign: 'center' }}>{t('auth:login.forgot')}</Link>}
      </form>
    </AuthCard>
  );
}
